import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    IconButton,
    LinearProgress,
    Paper,
    Popover,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {DateRangeOutlined} from "@mui/icons-material";
import {axiosInstance} from "@refinedev/simple-rest";
import {useTranslate} from "@refinedev/core";
import {DateRange} from "react-date-range";
import ko from "date-fns/locale/ko";
import {addDays} from "date-fns";
import dayjs from "dayjs";
import {API_URL} from "../../constants";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

type Answer = "만족" | "보통" | "불만";

interface AnswerSummary {
    answer: Answer;
    count: number;
    percent: number;
}

interface ServiceEvaluationSummary {
    total: number;
    averageRating: number;
    questions: Record<string, AnswerSummary[]>;
    byAction: {
        actionType: string;
        count: number;
    }[];
    daily: {
        date: string;
        count: number;
    }[];
}

const questionLabels: Record<string, string> = {
    qrMenuSatisfaction: "QR 실행 및 메뉴 구성이 보기 편하십니까?",
    designLayoutSatisfaction: "디자인 구성요소가 적절히 배치되어 보기에 편리하십니까?",
    arResponseSpeedSatisfaction: "증강현실 콘텐츠 응답속도는 적절합니까?",
    overallSatisfaction: "증강현실 콘텐츠 전체적인 만족도는 어떠십니까?",
};

const answerColors: Record<Answer, string> = {
    만족: "#1976d2",
    보통: "#ed6c02",
    불만: "#d32f2f",
};

const actionLabels: Record<string, string> = {
    download: "다운로드",
    share: "공유",
};

export const ServiceEvaluationPage: React.FC = () => {
    const t = useTranslate();
    const [summary, setSummary] = useState<ServiceEvaluationSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [focusedDateRange, setFocusedDateRange] = useState<[number, number]>([0, 0]);
    const [state, setState] = useState([
        {
            startDate: addDays(new Date(), -30),
            endDate: new Date(),
            key: "selection",
        },
    ]);

    const dateText = useMemo(() => {
        return `${dayjs(state[0].startDate).format("YYYY-MM-DD")} ~ ${dayjs(state[0].endDate).format("YYYY-MM-DD")}`;
    }, [state]);

    const fetchSummary = async (startDate: string, endDate: string) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get<ServiceEvaluationSummary>(
                `${API_URL}/service-evaluation/summary?startDate=${startDate}&endDate=${endDate}`,
                {
                    withCredentials: false,
                    headers: {
                        "Access-Control-Allow-Origin": "*",
                    },
                },
            );
            setSummary(res.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchSummary(
            dayjs(state[0].startDate).format("YYYY-MM-DD"),
            dayjs(state[0].endDate).format("YYYY-MM-DD"),
        );
        // eslint-disable-next-line
    }, []);

    const open = Boolean(anchorEl);
    const calId = open ? "service-evaluation-date-range-picker" : undefined;

    const total = summary?.total || 0;

    return (
        <Paper sx={{padding: 4}}>
            <Stack spacing={3}>
                <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t("", "서비스 평가 결과")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                            {t("", "사용자 AR 체험 후 제출된 시스템 UI/UX 조사 결과입니다.")}
                        </Typography>
                    </Box>
                    <TextField
                        size="small"
                        style={{width: "260px"}}
                        value={dateText}
                        InputProps={{
                            endAdornment: (
                                <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
                                    <DateRangeOutlined/>
                                </IconButton>
                            )
                        }}
                    />
                </Stack>

                <Popover
                    id={calId}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                >
                    <DateRange
                        locale={ko}
                        editableDateInputs={true}
                        onChange={(item: any) => {
                            setState([item.selection]);
                            if (focusedDateRange[1] === 1) {
                                void fetchSummary(
                                    dayjs(item.selection.startDate).format("YYYY-MM-DD"),
                                    dayjs(item.selection.endDate).format("YYYY-MM-DD"),
                                );
                                setAnchorEl(null);
                            }
                        }}
                        moveRangeOnFirstSelection={false}
                        retainEndDateOnFirstSelection={false}
                        ranges={state}
                        months={2}
                        dateDisplayFormat="yyyy-MM-dd"
                        direction="horizontal"
                        showDateDisplay={false}
                        onRangeFocusChange={setFocusedDateRange}
                        maxDate={new Date()}
                    />
                </Popover>

                {loading && <LinearProgress/>}

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"},
                        gap: 2,
                    }}
                >
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Typography variant="body2" color="text.secondary">
                            {t("", "총 응답 수")}
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{mt: 1}}>
                            {total.toLocaleString()}
                        </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Typography variant="body2" color="text.secondary">
                            {t("", "전체 만족도 평균")}
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{mt: 1}}>
                            {(summary?.averageRating || 0).toFixed(1)}
                        </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Typography variant="body2" color="text.secondary">
                            {t("", "조회 기간")}
                        </Typography>
                        <Typography variant="h6" fontWeight={700} sx={{mt: 1}}>
                            {dateText}
                        </Typography>
                    </Paper>
                </Box>

                <TableContainer>
                    <Table sx={{minWidth: 900}}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("", "문항")}</TableCell>
                                <TableCell align="right">{t("", "만족")}</TableCell>
                                <TableCell align="right">{t("", "보통")}</TableCell>
                                <TableCell align="right">{t("", "불만")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(questionLabels).map(([key, label]) => {
                                const answers = summary?.questions?.[key] || [];
                                const map = new Map(answers.map((item) => [item.answer, item]));
                                return (
                                    <TableRow key={key}>
                                        <TableCell component="th" scope="row" sx={{width: "42%"}}>
                                            {label}
                                        </TableCell>
                                        {(["만족", "보통", "불만"] as Answer[]).map((answer) => {
                                            const item = map.get(answer) || {answer, count: 0, percent: 0};
                                            return (
                                                <TableCell key={answer} align="right">
                                                    <Stack spacing={0.75}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {item.count.toLocaleString()}건 ({item.percent}%)
                                                        </Typography>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={item.percent}
                                                            sx={{
                                                                height: 8,
                                                                borderRadius: 1,
                                                                backgroundColor: "grey.200",
                                                                "& .MuiLinearProgress-bar": {
                                                                    backgroundColor: answerColors[answer],
                                                                },
                                                            }}
                                                        />
                                                    </Stack>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Stack direction={{xs: "column", md: "row"}} spacing={3}>
                    <TableContainer component={Paper} variant="outlined" sx={{flex: 1}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t("", "완료 액션")}</TableCell>
                                    <TableCell align="right">{t("", "응답 수")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(summary?.byAction || []).map((item) => (
                                    <TableRow key={item.actionType || "unknown"}>
                                        <TableCell>{actionLabels[item.actionType] || item.actionType || "-"}</TableCell>
                                        <TableCell align="right">{item.count.toLocaleString()}</TableCell>
                                    </TableRow>
                                ))}
                                {(!summary?.byAction || summary.byAction.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">{t("", "응답 데이터가 없습니다.")}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TableContainer component={Paper} variant="outlined" sx={{flex: 1}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t("", "일자")}</TableCell>
                                    <TableCell align="right">{t("", "응답 수")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(summary?.daily || []).map((item) => (
                                    <TableRow key={item.date}>
                                        <TableCell>{item.date}</TableCell>
                                        <TableCell align="right">{item.count.toLocaleString()}</TableCell>
                                    </TableRow>
                                ))}
                                {(!summary?.daily || summary.daily.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">{t("", "응답 데이터가 없습니다.")}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </Stack>
        </Paper>
    );
};
